import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import DeleteIcon from '@material-design-icons/svg/filled/delete.svg?react';
import EditIcon from '@material-design-icons/svg/filled/edit.svg?react';
import GroupIcon from '@material-design-icons/svg/filled/group.svg?react';
import { isLeft, isRight } from 'fp-ts/Either';
import { use, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { SearchableMultiSelect } from '../SearchableMultiSelect';
import { TRPCContext } from '../../contexts/TRPC';
import { captureAndReportError } from '../../utils/errorReporting';

interface PresetCapabilities {
  canReviewNewApps: boolean;
  canReviewUpdates: boolean;
  canApprove: boolean;
  canRejectDirectly: boolean;
  canReassignTickets: boolean;
  canViewAuditTrail: boolean;
  canAssignCategories: boolean;
  canPublish: boolean;
  requiresReviewChecklist: boolean;
}

interface PresetSummary {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  rejectionRoutePresetId: string | null;
  rejectionRoutePresetName: string | null;
  assignedUserCount: number;
  capabilities: PresetCapabilities;
}

interface PresetDetail extends PresetSummary {
  hiddenDapps: Array<{
    id: string;
    dappName: string;
    packageName: string | null;
    status: string;
  }>;
  assignedUsers: Array<{
    id: string;
    email: string;
    fullName: string;
  }>;
}

interface DappChoice {
  id: string;
  dappName: string;
  packageName: string | null;
  status: string;
}

const DEFAULT_CAPABILITIES: PresetCapabilities = {
  canReviewNewApps: false,
  canReviewUpdates: true,
  canApprove: true,
  canRejectDirectly: false,
  canReassignTickets: false,
  canViewAuditTrail: false,
  canAssignCategories: false,
  canPublish: false,
  requiresReviewChecklist: true,
};

const CAPABILITY_LABELS: Record<keyof PresetCapabilities, string> = {
  canReviewNewApps: 'Review new app submissions',
  canReviewUpdates: 'Review app updates',
  canApprove: 'Approve tickets',
  canRejectDirectly: 'Reject tickets directly',
  canReassignTickets: 'Reassign tickets to others',
  canViewAuditTrail: 'View audit trail',
  canAssignCategories: 'Assign categories',
  canPublish: 'Publish apps',
  requiresReviewChecklist: 'Require review checklist',
};

const GENERAL_CAPABILITY_KEYS: Array<keyof PresetCapabilities> = [
  'canReviewNewApps',
  'canReviewUpdates',
  'canApprove',
  'canReassignTickets',
  'canViewAuditTrail',
  'canAssignCategories',
  'canPublish',
  'requiresReviewChecklist',
];

interface PresetFormState {
  name: string;
  description: string;
  isDefault: boolean;
  rejectionRoutePresetId: string;
  capabilities: PresetCapabilities;
  hiddenDappIds: string[];
}

const emptyForm = (): PresetFormState => ({
  name: '',
  description: '',
  isDefault: false,
  rejectionRoutePresetId: '',
  capabilities: { ...DEFAULT_CAPABILITIES },
  hiddenDappIds: [],
});

function PresetCard({
  preset,
  onEdit,
  onDelete,
  onViewUsers,
}: {
  preset: PresetSummary;
  onEdit: () => void;
  onDelete: () => void;
  onViewUsers: () => void;
}) {
  const enabledCaps = (
    Object.entries(preset.capabilities) as Array<
      [keyof PresetCapabilities, boolean]
    >
  ).filter(([, v]) => v);

  return (
    <div className="rounded-lg border border-neutral-800/70 bg-neutral-800/30 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white truncate">
              {preset.name}
            </h3>
            {preset.isDefault && (
              <span className="shrink-0 rounded-full bg-teal-500/10 px-2 py-0.5 text-[10px] font-medium text-teal-300 ring-1 ring-inset ring-teal-500/30">
                Default
              </span>
            )}
          </div>
          {preset.description && (
            <p className="mt-0.5 text-xs text-neutral-400 line-clamp-2">
              {preset.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
            title="Edit preset"
          >
            <EditIcon className="size-4 fill-current" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={preset.assignedUserCount > 0}
            className="p-1.5 rounded-md text-neutral-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={
              preset.assignedUserCount > 0
                ? 'Reassign users before deleting'
                : 'Delete preset'
            }
          >
            <DeleteIcon className="size-4 fill-current" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-neutral-400">
        <button
          type="button"
          onClick={onViewUsers}
          disabled={preset.assignedUserCount === 0}
          className="flex items-center gap-1 hover:text-teal-400 transition-colors disabled:hover:text-neutral-400 disabled:cursor-default"
        >
          <GroupIcon className="size-3.5 fill-current" />
          {preset.assignedUserCount}{' '}
          {preset.assignedUserCount === 1 ? 'user' : 'users'}
        </button>
        {preset.rejectionRoutePresetName && (
          <span>Rejects &rarr; {preset.rejectionRoutePresetName}</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1">
        {enabledCaps.map(([key]) => (
          <span
            key={key}
            className="rounded bg-neutral-700/50 px-1.5 py-0.5 text-[10px] text-neutral-300"
          >
            {CAPABILITY_LABELS[key]}
          </span>
        ))}
      </div>
    </div>
  );
}

function PresetUsersModal({
  presetName,
  users,
  onClose,
}: {
  presetName: string;
  users: Array<{ id: string; email: string; fullName: string }>;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[80vh] overflow-y-auto rounded-xl border border-neutral-800/60 bg-neutral-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-neutral-800 bg-neutral-900 px-6 py-4">
          <h3 className="text-base font-semibold text-white">
            {presetName} — Assigned Users
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-neutral-400 hover:text-white transition-colors"
          >
            <CloseIcon className="size-5 fill-current" />
          </button>
        </div>
        <div className="px-6 py-4">
          {users.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-4">
              No users assigned to this preset.
            </p>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-3 rounded-lg border border-neutral-800/60 bg-neutral-800/20 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {user.fullName}
                    </div>
                    <div className="text-xs text-neutral-400 truncate">
                      {user.email}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PresetModal({
  form,
  presets,
  dappChoices,
  editingPresetId,
  isSaving,
  onFormChange,
  onSave,
  onClose,
}: {
  form: PresetFormState;
  presets: PresetSummary[];
  dappChoices: Array<{ key: string; label: string }>;
  editingPresetId: string | null;
  isSaving: boolean;
  onFormChange: (updates: Partial<PresetFormState>) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const routeOptions = presets.filter((p) => p.id !== editingPresetId);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl border border-neutral-800/60 bg-neutral-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-neutral-800 bg-neutral-900 px-6 py-4">
          <h3 className="text-base font-semibold text-white">
            {editingPresetId ? 'Edit Preset' : 'Create Preset'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-neutral-400 hover:text-white transition-colors"
          >
            <CloseIcon className="size-5 fill-current" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
                Name
              </span>
              <input
                aria-label="Name"
                value={form.name}
                onChange={(e) => onFormChange({ name: e.target.value })}
                maxLength={100}
                placeholder="e.g. Senior Reviewer"
                className="w-full rounded-lg border border-neutral-700/70 bg-neutral-800/60 px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>
            <label className="space-y-1.5">
              <span className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
                Description
              </span>
              <input
                aria-label="Description"
                value={form.description}
                onChange={(e) => onFormChange({ description: e.target.value })}
                maxLength={500}
                placeholder="Optional description"
                className="w-full rounded-lg border border-neutral-700/70 bg-neutral-800/60 px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              aria-label="Default preset"
              checked={form.isDefault}
              onChange={(e) => onFormChange({ isDefault: e.target.checked })}
              className="rounded border-neutral-600 bg-neutral-800 text-teal-500 focus:ring-teal-500"
            />
            <span className="text-sm text-neutral-300">Default preset</span>
          </label>

          <div className="space-y-2">
            <span className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
              Capabilities
            </span>
            <div className="grid gap-2 sm:grid-cols-2">
              {GENERAL_CAPABILITY_KEYS.map((key) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    aria-label={CAPABILITY_LABELS[key]}
                    checked={form.capabilities[key]}
                    onChange={(e) =>
                      onFormChange({
                        capabilities: {
                          ...form.capabilities,
                          [key]: e.target.checked,
                        },
                      })
                    }
                    className="rounded border-neutral-600 bg-neutral-800 text-teal-500 focus:ring-teal-500"
                  />
                  <span className="text-sm text-neutral-300">
                    {CAPABILITY_LABELS[key]}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-neutral-800/70 bg-neutral-800/20 p-4">
            <span className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
              Rejection handling
            </span>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label="Reject tickets directly"
                checked={form.capabilities.canRejectDirectly}
                onChange={(e) =>
                  onFormChange({
                    capabilities: {
                      ...form.capabilities,
                      canRejectDirectly: e.target.checked,
                    },
                  })
                }
                className="rounded border-neutral-600 bg-neutral-800 text-teal-500 focus:ring-teal-500"
              />
              <span className="text-sm text-neutral-300">
                Reject tickets directly
              </span>
            </label>
            <label className="space-y-1.5">
              <span className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
                Rejection route
              </span>
              <select
                value={form.rejectionRoutePresetId}
                disabled={form.capabilities.canRejectDirectly}
                onChange={(e) =>
                  onFormChange({ rejectionRoutePresetId: e.target.value })
                }
                className="w-full rounded-lg border border-neutral-700/70 bg-neutral-800/60 px-3 py-2.5 text-sm text-white focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <option value="">Global fallback</option>
                {routeOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {form.capabilities.canRejectDirectly && (
                <p className="text-xs text-neutral-500">
                  Not applicable when direct rejection is enabled.
                </p>
              )}
            </label>
          </div>

          <div className="space-y-2">
            <span className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
              Hidden apps
            </span>
            <SearchableMultiSelect
              name="preset-hidden-apps"
              choices={dappChoices}
              value={form.hiddenDappIds}
              placeholder="Select apps to hide from this preset"
              searchPlaceholder="Search apps..."
              maxVisibleSelections={6}
              disabled={isSaving}
              onChange={(ids) => onFormChange({ hiddenDappIds: ids })}
            />
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-neutral-800 bg-neutral-900 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-neutral-300 bg-neutral-800/60 hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving || !form.name.trim()}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving…' : editingPresetId ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ModeratorPresetManagementSection() {
  const trpc = use(TRPCContext);
  const [presets, setPresets] = useState<PresetSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [form, setForm] = useState<PresetFormState>(emptyForm());
  const [isSaving, setIsSaving] = useState(false);
  const [dappChoices, setDappChoices] = useState<
    Array<{ key: string; label: string }>
  >([]);
  const [usersModalPreset, setUsersModalPreset] = useState<{
    name: string;
    users: Array<{ id: string; email: string; fullName: string }>;
  } | null>(null);

  const loadPresets = useCallback(async () => {
    try {
      const result = await trpc.client.listModeratorPresets.query(undefined);
      if (isRight(result)) {
        setPresets(result.right as PresetSummary[]);
      }
    } catch (err) {
      captureAndReportError(err, {
        operation: 'admin.loadModeratorPresets',
      });
      toast.error('Failed to load presets');
    } finally {
      setIsLoading(false);
    }
  }, [trpc.client]);

  const loadDappChoices = useCallback(async () => {
    try {
      const result =
        await trpc.client.getHubspotTicketModeratorSettings.query(undefined);
      if (isRight(result)) {
        const settings = result.right as { dapps: DappChoice[] };
        setDappChoices(
          settings.dapps.map((d) => ({
            key: d.id,
            label: d.packageName
              ? `${d.dappName} (${d.packageName})`
              : d.dappName,
          })),
        );
      }
    } catch {
      /* dapp list is best-effort for the form */
    }
  }, [trpc.client]);

  const viewPresetUsers = async (presetId: string) => {
    try {
      const result = await trpc.client.getModeratorPreset.query({ presetId });
      if (isRight(result)) {
        const detail = result.right as PresetDetail;
        setUsersModalPreset({
          name: detail.name,
          users: detail.assignedUsers,
        });
      } else if (isLeft(result)) {
        toast.error(result.left.message || 'Failed to load preset users');
      }
    } catch (err) {
      captureAndReportError(err, {
        operation: 'admin.viewPresetUsers',
        presetId,
      });
      toast.error('Failed to load preset users');
    }
  };

  useEffect(() => {
    void loadPresets();
    void loadDappChoices();
  }, [loadPresets, loadDappChoices]);

  const openCreateModal = () => {
    setEditingPresetId(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const openEditModal = async (presetId: string) => {
    try {
      const result = await trpc.client.getModeratorPreset.query({
        presetId,
      });
      if (isRight(result)) {
        const detail = result.right as PresetDetail;
        setEditingPresetId(presetId);
        setForm({
          name: detail.name,
          description: detail.description ?? '',
          isDefault: detail.isDefault,
          rejectionRoutePresetId: detail.rejectionRoutePresetId ?? '',
          capabilities: { ...detail.capabilities },
          hiddenDappIds: detail.hiddenDapps.map((d) => d.id),
        });
        setIsModalOpen(true);
      } else if (isLeft(result)) {
        toast.error(result.left.message || 'Failed to load preset');
      }
    } catch (err) {
      captureAndReportError(err, {
        operation: 'admin.getModeratorPreset',
        presetId,
      });
      toast.error('Failed to load preset');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPresetId(null);
    setForm(emptyForm());
  };

  const handleDelete = async (preset: PresetSummary) => {
    if (preset.assignedUserCount > 0) {
      toast.error('Reassign all users before deleting this preset');
      return;
    }

    if (
      !window.confirm(`Delete preset "${preset.name}"? This cannot be undone.`)
    ) {
      return;
    }

    try {
      const result = await trpc.client.deleteModeratorPreset.mutate({
        presetId: preset.id,
      });
      if (isRight(result)) {
        toast.success(`Preset "${preset.name}" deleted`);
        await loadPresets();
      } else if (isLeft(result)) {
        toast.error(result.left.message || 'Failed to delete preset');
      }
    } catch (err) {
      captureAndReportError(err, {
        operation: 'admin.deleteModeratorPreset',
        presetId: preset.id,
      });
      toast.error('Failed to delete preset');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        isDefault: form.isDefault,
        rejectionRoutePresetId: form.rejectionRoutePresetId || null,
        capabilities: form.capabilities,
        hiddenDappIds: form.hiddenDappIds,
      };

      const result = editingPresetId
        ? await trpc.client.updateModeratorPreset.mutate({
            presetId: editingPresetId,
            ...payload,
          })
        : await trpc.client.createModeratorPreset.mutate(payload);

      if (isRight(result)) {
        toast.success(editingPresetId ? 'Preset updated' : 'Preset created');
        closeModal();
        await loadPresets();
      } else if (isLeft(result)) {
        toast.error(result.left.message || 'Failed to save preset');
      }
    } catch (err) {
      captureAndReportError(err, {
        operation: editingPresetId
          ? 'admin.updateModeratorPreset'
          : 'admin.createModeratorPreset',
      });
      toast.error('Failed to save preset');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormChange = (updates: Partial<PresetFormState>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="size-5 animate-spin rounded-full border-2 border-neutral-700 border-t-neutral-300" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-800/80 px-2 py-0.5 text-xs font-medium text-neutral-300 ring-1 ring-inset ring-neutral-700/60">
            {presets.length} {presets.length === 1 ? 'preset' : 'presets'}
          </span>
          <button
            type="button"
            onClick={openCreateModal}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-teal-400 hover:text-teal-300 rounded-lg transition-colors"
          >
            <AddIcon className="size-4 fill-current" />
            Create Preset
          </button>
        </div>

        {presets.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-neutral-400">
              No presets configured. Create one to start customizing moderator
              capabilities.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {presets.map((preset) => (
              <PresetCard
                key={preset.id}
                preset={preset}
                onEdit={() => {
                  void openEditModal(preset.id);
                }}
                onDelete={() => {
                  void handleDelete(preset);
                }}
                onViewUsers={() => {
                  void viewPresetUsers(preset.id);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {isModalOpen &&
        createPortal(
          <PresetModal
            form={form}
            presets={presets}
            dappChoices={dappChoices}
            editingPresetId={editingPresetId}
            isSaving={isSaving}
            onFormChange={handleFormChange}
            onSave={() => {
              void handleSave();
            }}
            onClose={closeModal}
          />,
          document.body,
        )}

      {usersModalPreset &&
        createPortal(
          <PresetUsersModal
            presetName={usersModalPreset.name}
            users={usersModalPreset.users}
            onClose={() => setUsersModalPreset(null)}
          />,
          document.body,
        )}
    </>
  );
}
